import { buildExpandCollapseViewConfig } from '@components/FilterV3/components/ExpandCollapseButtonGroup/expandCollapseButtonGroup.utils';
import useWidth from '@components/Hooks/useWidth';
import { useObjectContext } from '@components/v2/layout/ObjectScreen/hooks/useObjectContext';
import { useState } from 'react';
import LangEnum from '../../../../../../../lang/LangEnum';
import {
  useOrderTargets,
  useTargetBreakdownsByTargetIds,
  useTargetDetailsByTargetIds,
} from '../../../../../../../services/modules/target/target.queries';
import { toggleSetValue } from '../../../../../../../utils/set.utils';
import { OrderObjectTargetsTestIds } from '../../../../../../../utils/testIds/OrderObject.testIds';
import { UseOrderObjectTargetsTabParams } from '../orderObjectTargetsTab.types';
import {
  buildOrderTargetRows,
  getExpandableIds,
  getOrderTargetUnits,
} from '../orderObjectTargetsTab.utils';
import useOrderTargetsColumns from './useOrderTargetsColumns';
import useOrderTargetsExpandedIds from './useOrderTargetsExpandedIds';

const useOrderObjectTargetsTab = ({ orderId }: UseOrderObjectTargetsTabParams) => {
  const { data, isLoading, isError, refetch } = useOrderTargets(orderId);
  const targets = data ?? [];
  const targetIds = targets.map((target) => target.id);
  const breakdowns = useTargetBreakdownsByTargetIds(targetIds);
  const [revealedIds, setRevealedIds] = useState<Set<string>>(new Set());
  const details = useTargetDetailsByTargetIds(targetIds, revealedIds);
  const { expandedIds, setExpandedIds } = useOrderTargetsExpandedIds();
  const { scrollParent } = useObjectContext();
  const { smallWidth } = useWidth();

  const rows = buildOrderTargetRows({ targets, breakdowns, details, expandedIds });

  const revealSummary = (targetId: string) =>
    setRevealedIds((current) => (current.has(targetId) ? current : new Set(current).add(targetId)));

  const columns = useOrderTargetsColumns({
    units: getOrderTargetUnits(targets),
    isCompact: smallWidth,
    onToggle: (rowId) => setExpandedIds((current) => toggleSetValue(current, rowId)),
    onRevealSummary: revealSummary,
  });

  const changeExpansion = (isExpanded: boolean) =>
    setExpandedIds(isExpanded ? getExpandableIds({ targets, breakdowns }) : new Set());

  const hasListError = isError && !data;
  const hasBreakdownError = breakdowns.some((breakdown) => breakdown.isError && !breakdown.items);

  const reload = () => {
    refetch();
    breakdowns.forEach((breakdown) => breakdown.refetch());
    details.forEach((entry) => entry.refetch());
  };

  const viewConfigs = [
    buildExpandCollapseViewConfig({
      label: LangEnum.GLOBAL_PLANS,
      isExpanded: expandedIds.size > 0,
      defaultValue: false,
      collapseTestId: OrderObjectTargetsTestIds.COLLAPSE_ALL_BUTTON,
      expandTestId: OrderObjectTargetsTestIds.EXPAND_ALL_BUTTON,
      onApply: changeExpansion,
    }),
  ];

  return {
    rows,
    columns,
    viewConfigs,
    scrollParent,
    isLoading: isLoading && !data,
    isError: hasListError || hasBreakdownError,
    errorTitle: hasListError
      ? LangEnum.ORDER_OBJECT_TARGETS_ERROR
      : LangEnum.ORDER_OBJECT_TARGETS_BREAKDOWNS_ERROR,
    isEmpty: !!data && data.length === 0,
    refetch: reload,
  };
};

export default useOrderObjectTargetsTab;
